import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import OrnamentBackground from '../../components/OrnamentBackground';
import biometricService from '../../services/biometricService';

import { createLogger } from '../../utils/logger';
const logger = createLogger('BiometricSetupPrompt');

interface BiometricSetupPromptScreenProps {
  navigation: NavigationProp<any>;
}

const BiometricSetupPromptScreen: React.FC<BiometricSetupPromptScreenProps> = ({
  navigation,
}) => {
  const [biometricType, setBiometricType] = useState<string | null>(null);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      const capabilities = await biometricService.isBiometricAvailable();
      setIsAvailable(capabilities.available);

      if (capabilities.available) {
        const type = await biometricService.getBiometricType();
        setBiometricType(type);
      }
    } catch (error) {
      logger.error('Error checking biometric availability:', error);
      setIsAvailable(false);
    }
  };

  const handleEnableBiometric = async () => {
    setIsEnabling(true);

    try {
      // Get stored credentials
      const userJson = await AsyncStorage.getItem('user');
      const storedEmail = await AsyncStorage.getItem('@signup_email');

      if (!userJson && !storedEmail) {
        Alert.alert('Error', 'Unable to enable biometric authentication. Please try again later.');
        navigateToDashboard();
        return;
      }

      const user = userJson ? JSON.parse(userJson) : null;
      const email = user?.email || storedEmail;

      // For security, we need the user to re-enter their password
      Alert.prompt(
        'Enable Biometric Login',
        `Please enter your password to enable ${biometricType} authentication`,
        [
          {
            text: 'Cancel',
            style: 'cancel',
            onPress: () => setIsEnabling(false),
          },
          {
            text: 'Enable',
            onPress: async (password) => {
              if (!password) {
                Alert.alert('Error', 'Password is required');
                setIsEnabling(false);
                return;
              }

              try {
                const result = await biometricService.enableBiometricLogin(email, password);

                if (result.success) {
                  await AsyncStorage.setItem('biometricEnabled', 'true');
                  Alert.alert(
                    'Success',
                    `${biometricType} authentication has been enabled successfully!`,
                    [{ text: 'OK', onPress: navigateToDashboard }]
                  );
                } else {
                  Alert.alert('Error', result.error || 'Failed to enable biometric authentication');
                  setIsEnabling(false);
                }
              } catch (error) {
                logger.error('Error enabling biometric:', error);
                Alert.alert('Error', 'Failed to enable biometric authentication. Please try again.');
                setIsEnabling(false);
              }
            },
          },
        ],
        'secure-text'
      );
    } catch (error) {
      logger.error('Error in biometric setup:', error);
      Alert.alert('Error', 'Failed to enable biometric authentication. Please try again.');
      setIsEnabling(false);
    }
  };

  const handleSkip = () => {
    navigateToDashboard();
  };

  const navigateToDashboard = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: 'Dashboard' }],
    });
  };

  if (!isAvailable) {
    // If biometrics are not available, skip to dashboard automatically
    useEffect(() => {
      navigateToDashboard();
    }, []);

    return (
      <SafeAreaView style={styles.container}>
        <OrnamentBackground />
        <View style={styles.content}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />

      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <Image
            source={require('../../assets/icons/faceIdIcon.png')}
            style={styles.biometricIcon}
          />
        </View>

        <View style={styles.header}>
          <Text style={styles.title}>Enable {biometricType}?</Text>
          <Text style={styles.subtitle}>
            Use {biometricType} for quick and secure login to your account
          </Text>
        </View>

        <View style={styles.benefitsContainer}>
          <Text style={styles.benefitsTitle}>Benefits:</Text>

          <View style={styles.benefitItem}>
            <Text style={styles.bulletPoint}>✓</Text>
            <Text style={styles.benefitText}>
              Quick login without typing your password
            </Text>
          </View>

          <View style={styles.benefitItem}>
            <Text style={styles.bulletPoint}>✓</Text>
            <Text style={styles.benefitText}>
              Enhanced security using your unique biometric data
            </Text>
          </View>

          <View style={styles.benefitItem}>
            <Text style={styles.bulletPoint}>✓</Text>
            <Text style={styles.benefitText}>
              No need to remember complex passwords
            </Text>
          </View>

          <View style={styles.benefitItem}>
            <Text style={styles.bulletPoint}>✓</Text>
            <Text style={styles.benefitText}>
              Your biometric data stays secure on your device
            </Text>
          </View>
        </View>

        <View style={styles.infoBox}>
          <Text style={styles.infoText}>
            You can always enable or disable {biometricType} later in your account settings.
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.enableButton, isEnabling && styles.enableButtonDisabled]}
          onPress={handleEnableBiometric}
          disabled={isEnabling}
        >
          {isEnabling ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.enableButtonText}>Enable {biometricType}</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.skipButton}
          onPress={handleSkip}
          disabled={isEnabling}
        >
          <Text style={styles.skipButtonText}>Skip for Now</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  content: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    marginTop: ms(60),
    paddingHorizontal: ms(20),
    paddingTop: ms(40),
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: ms(20),
  },
  biometricIcon: {
    width: s(80),
    height: s(80),
  },
  header: {
    alignItems: 'center',
    marginBottom: ms(30),
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(24, 0.3),
    paddingHorizontal: ms(20),
  },
  benefitsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
  },
  benefitsTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#333',
    marginBottom: ms(15),
  },
  benefitItem: {
    flexDirection: 'row',
    marginBottom: ms(12),
  },
  bulletPoint: {
    fontSize: ms(18, 0.3),
    color: '#29BFC0',
    fontWeight: 'bold',
    marginRight: ms(10),
    width: s(20),
  },
  benefitText: {
    fontSize: ms(15, 0.3),
    color: '#666',
    flex: 1,
    lineHeight: ms(22, 0.3),
  },
  infoBox: {
    backgroundColor: '#E3F2FD',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(25),
  },
  infoText: {
    fontSize: ms(14, 0.3),
    color: '#1565C0',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  enableButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(15),
  },
  enableButtonDisabled: {
    opacity: 0.6,
  },
  enableButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  skipButton: {
    backgroundColor: 'transparent',
    paddingVertical: ms(16),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  skipButtonText: {
    color: '#666',
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
});

export default BiometricSetupPromptScreen;
